import { useState } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { MOTORES, provedorOpencodeNaoTratado, ROTULO_DO_MOTOR, type Motor } from '@remoto/protocolo';
import {
  pastaCurta,
  PESO_CODEX,
  type ContaDoPainel,
  type SessaoDoPainel,
  type UsoPainel,
} from '@remoto/shared';
import { rotaPainel } from './painel';
import { Cartao } from '../componentes/layout';
import { CampoDeEscolha } from '../componentes/campos';
import { Modal } from '../componentes/Modal';
import { BarraDeCota, MedidorDeCota } from '../componentes/MedidorDeCota';
import { IconeAviso, IconeMestre } from '../componentes/icones';
import { SeloAssinatura } from '../componentes/SeloAssinatura';
import { SeloForaDeCasa } from '../componentes/SeloForaDeCasa';
import { Tela } from '../workspace/Tela';
import { mensagemDoErro } from '../erros';
import { porcento, tempoRelativo } from '../formato';
import { usarUsoDoPainel } from '../uso/consultas';
import {
  assinaturaNaTela,
  cartaoSempreVisivel,
  contextoEmTexto,
  custoEmTexto,
  dadoVelho,
  formaDaConta,
  ordenarJanelas,
  pesoNaOrdem,
  rotuloDaJanela,
  rotuloDoCusto,
  textoSemCota,
  tituloDoOpencode,
} from '../uso/cota';

/**
 * A tela de Uso responde, sem rolar, três perguntas POR CONTA: quanto da semana
 * ela já usou, quando reinicia, e quem gastou (ADR-0021).
 *
 * O molde é o `/usage` do Claude Code — a informação e a ordem, não o visual
 * de terminal. Desde 2026-09-24 (ADR-0024) a frota tem mais de uma conta por
 * motor — a VPS e o PC podem estar em contas Claude diferentes —, e por isso a
 * tela é UM CARTÃO POR CONTA: a cota dela, as máquinas logadas nela e as
 * sessões que gastaram nela, com o % sobre ela. Somar sessões de contas
 * diferentes num rateio só daria um número que não é de conta nenhuma. O
 * OpenCode é uma conta só (a chave do provedor de modelos da frota), sem
 * rateio: no OpenRouter o cartão mostra o gasto real da semana; no NanoGPT
 * (F2, D43), a assinatura da conta, o crédito e o equivalente da semana.
 */

/** O nome da conta, como o dono a reconhece: o motor e o e-mail (no OpenCode, o provedor). */
function tituloDaConta(conta: ContaDoPainel): string {
  if (formaDaConta(conta.motor) === 'custo-real') return tituloDoOpencode(conta.provedorOpencode);
  return `${ROTULO_DO_MOTOR[conta.motor]} · ${conta.email ?? 'conta não identificada'}`;
}

/** Onde a conta está logada agora — ou por que o cartão existe sem máquina. */
function MaquinasDaConta({ conta }: { conta: ContaDoPainel }): React.JSX.Element {
  if (conta.email === null) {
    return (
      <p className="text-xs text-zinc-500">
        Uso gravado antes de a máquina informar a conta (daemon anterior à 0.7.1). Não se sabe de
        qual conta ele foi, então fica sem % — nunca na conta errada.
      </p>
    );
  }
  if (conta.maquinas.length === 0) {
    return (
      <p className="text-xs text-zinc-500">
        Nenhuma máquina logada nesta conta agora. O uso que ela teve na semana continua aqui.
      </p>
    );
  }
  return (
    <p data-testid="maquinas-da-conta" className="min-w-0 truncate text-xs text-zinc-400">
      <span className="text-zinc-600">logada em </span>
      {conta.maquinas.map((m) => m.nome + (m.perfil ? ` (${m.perfil})` : '')).join(', ')}
    </p>
  );
}

function CotaDaConta({ conta }: { conta: ContaDoPainel }): React.JSX.Element | null {
  // A conta não identificada só tem cota na transição (nenhuma conta informada ainda).
  if (conta.email === null && conta.janelas.length === 0) return null;
  const janelas = ordenarJanelas(conta.janelas);
  const velho = dadoVelho(conta.atualizadoEm);
  return (
    <div className="space-y-3">
      <p className={'flex items-center gap-1.5 text-xs ' + (velho ? 'text-amber-300' : 'text-zinc-500')}>
        {velho ? <IconeAviso tamanho={13} /> : null}
        {conta.atualizadoEm ? `atualizado ${tempoRelativo(conta.atualizadoEm)}` : 'nenhuma leitura ainda'}
        {/*
          A cota não é sondada: ela chega junto das respostas do motor. Conta
          parada é leitura parada — e a frase diz por que, para ninguém ler um
          número de ontem como se fosse de agora.
        */}
        {velho ? ' — dado velho: a cota só é relida quando uma sessão desta conta roda' : null}
      </p>
      {janelas.length === 0 ? (
        <MedidorDeCota
          rotulo="Semana"
          utilizacao={null}
          reiniciaEm={null}
          detalhe="nenhuma sessão desta conta relatou cota ainda"
        />
      ) : (
        janelas.map((janela) => (
          <MedidorDeCota
            key={janela.id}
            rotulo={rotuloDaJanela(janela)}
            utilizacao={janela.utilizacao}
            reiniciaEm={janela.reiniciaEm}
          />
        ))
      )}
    </div>
  );
}

/**
 * A conta do OpenCode no OpenRouter (D20): sem cota, o que se sabe é o gasto
 * real da semana, somado do custo que o OpenRouter cobrou em cada turno. O
 * limite de verdade é o teto da própria chave, no painel do OpenRouter (D14).
 */
function CustoRealDaConta({ conta }: { conta: ContaDoPainel }): React.JSX.Element {
  const total = conta.totalUsdSemana;
  return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-500">gasto real nesta semana</p>
      <p data-testid="total-da-semana" className="text-2xl tabular-nums text-zinc-100">
        {total === undefined ? '—' : custoEmTexto(total)}
      </p>
      <p className="text-[11px] text-zinc-500">
        {total === undefined
          ? 'nenhuma chamada desta semana disse quanto custou'
          : 'somado do custo que o OpenRouter cobrou em cada turno'}
        . Não há cota: o limite é o teto da chave, no painel do OpenRouter.
      </p>
    </div>
  );
}

/** Um número do cartão do NanoGPT: rótulo pequeno em cima, valor, e o que ele quer dizer. */
function Numero({
  rotulo,
  valor,
  explicacao,
  testid,
}: {
  rotulo: string;
  valor: string;
  explicacao: string;
  testid: string;
}): React.JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-xs text-zinc-500">{rotulo}</p>
      <p data-testid={testid} className="truncate text-xl tabular-nums text-zinc-100">
        {valor}
      </p>
      <p className="text-[11px] text-zinc-500">{explicacao}</p>
    </div>
  );
}

/**
 * A conta do OpenCode no NanoGPT (F2, D43 e D45): TRÊS leituras, e o cuidado
 * é não deixar uma passar pela outra.
 *
 * 1. **A assinatura**: o % dos tokens de ENTRADA da semana, com a barra e o
 *    reinício — é ela que paga primeiro (D40). `degradada` é "não sei".
 * 2. **O crédito**: o saldo e o gasto em crédito da semana — o dinheiro de
 *    verdade, lido da conta do NanoGPT.
 * 3. **O equivalente da semana**: o `totalUsdSemana`, somado do preço de
 *    TABELA que o OpenCode relata em cada turno. Não é dinheiro: a chamada
 *    coberta pela assinatura sai de graça, e o número continua somando.
 *
 * Não é "motor de cota": a assinatura é um total da CONTA e mora aqui, nunca
 * vira % por sessão (`temCotaDeAssinatura(OPENCODE)` continua falso).
 */
function ContaDoNanogpt({ conta }: { conta: ContaDoPainel }): React.JSX.Element {
  const leitura = conta.contaNanogpt;
  const assinatura = assinaturaNaTela(leitura);
  const total = conta.totalUsdSemana;
  return (
    <div className="space-y-4">
      {leitura ? (
        <p className="text-xs text-zinc-500">lido do NanoGPT {tempoRelativo(leitura.lidoEm)}</p>
      ) : (
        <p data-testid="conta-nanogpt-ausente" className="flex items-center gap-1.5 text-xs text-amber-300">
          <IconeAviso tamanho={13} />
          Não deu para ler a conta do NanoGPT agora: a assinatura, o saldo e o gasto em crédito
          ficam sem número — não sei, e não zero.
        </p>
      )}
      {assinatura ? (
        <div data-testid="assinatura-nanogpt">
          <MedidorDeCota
            rotulo="Assinatura · semana"
            utilizacao={assinatura.utilizacao}
            reiniciaEm={assinatura.reiniciaEm}
            detalhe={assinatura.detalhe}
          />
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <Numero
          testid="saldo-nanogpt"
          rotulo="saldo de crédito"
          valor={custoEmTexto(leitura?.saldoUsd ?? null) ?? 'não sei'}
          explicacao="o que sobra na conta para quando a assinatura acaba"
        />
        <Numero
          testid="gasto-em-credito"
          rotulo="gasto em crédito na semana"
          valor={custoEmTexto(leitura?.gastoSemanaUsd ?? null) ?? 'não sei'}
          explicacao="dinheiro de verdade, dos últimos 7 dias; a chamada coberta pela assinatura entra com zero"
        />
        <Numero
          testid="total-da-semana"
          rotulo="equivalente da semana"
          valor={total === undefined ? '—' : (custoEmTexto(total) ?? '—')}
          explicacao={
            total === undefined
              ? 'nenhuma chamada desta semana disse quanto custaria'
              : 'em preço de tabela, somado dos turnos — não é o que foi cobrado'
          }
        />
      </div>
    </div>
  );
}

/**
 * O cartão do OpenCode, pelo provedor ATIVO que a API diz (F2). Ausente é a
 * API anterior à F2, quando o OpenRouter era o único — e o cartão de antes.
 */
function ContaDoOpencode({ conta }: { conta: ContaDoPainel }): React.JSX.Element {
  const provedor = conta.provedorOpencode;
  if (provedor === undefined) return <CustoRealDaConta conta={conta} />;
  switch (provedor) {
    case 'nanogpt':
      return <ContaDoNanogpt conta={conta} />;
    case 'openrouter':
      return <CustoRealDaConta conta={conta} />;
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}


type FiltroDeMotor = 'TODOS' | Motor;

const OPCOES_DE_MOTOR = [
  { id: 'TODOS' as const, rotulo: 'Todos os motores' },
  ...MOTORES.map((motor) => ({ id: motor, rotulo: ROTULO_DO_MOTOR[motor] })),
];

/**
 * Maior fatia primeiro; sem cota conhecida vai para o fim — "não se sabe" não
 * é "zero", mas também não é mais que ninguém. O peso só desempata dentro do
 * mesmo motor: peso do Claude é dólar e do Codex é token, e compará-los seria
 * comparar metro com litro. O OpenCode, sem cota, fica no fim, ordenado pelo
 * custo real (`pesoNaOrdem`).
 */
function porFatia(a: SessaoDoPainel, b: SessaoDoPainel): number {
  const fatia = (b.pctSemanal ?? -1) - (a.pctSemanal ?? -1);
  if (fatia !== 0) return fatia;
  return a.motor === b.motor ? pesoNaOrdem(b) - pesoNaOrdem(a) : 0;
}

/**
 * Uma célula que, no telefone, diz o próprio nome — lá não há cabeçalho de
 * coluna, e "US$ 150,00" solto no cartão não diz de quê.
 */
function Celula({ rotulo, children }: { rotulo: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <p className="min-w-0 truncate text-xs tabular-nums text-zinc-400">
      <span className="text-zinc-600 lg:sr-only">{rotulo} </span>
      {children}
    </p>
  );
}

/** Colunas da grade no desktop. Uma só definição para o cabeçalho e as linhas. */
const GRADE =
  'lg:grid lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1.3fr)_6rem_minmax(0,1fr)_4.5rem_6.5rem] lg:items-center lg:gap-3';

function LinhaDaSessao({ sessao }: { sessao: SessaoDoPainel }): React.JSX.Element {
  return (
    <li
      data-testid="sessao-do-uso"
      className={
        'grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-zinc-800/70 p-3 ' +
        'lg:rounded-none lg:border-0 lg:border-t lg:px-2 lg:py-2 ' +
        GRADE
      }
    >
      <div className="col-span-2 min-w-0 lg:col-span-1">
        <Link
          to="/sessoes/$id"
          params={{ id: sessao.sessaoId }}
          className="flex min-w-0 items-center gap-1.5 text-sm text-zinc-100 hover:underline"
        >
          <span className="truncate">{sessao.titulo}</span>
          {sessao.funcao === 'MESTRE' ? (
            <span
              data-testid="selo-mestre"
              className="inline-flex shrink-0 items-center gap-1 rounded bg-sky-400/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-300"
            >
              <IconeMestre tamanho={10} />
              mestre
            </span>
          ) : null}
        </Link>
        <p className="truncate text-[11px] text-zinc-500" title={sessao.projeto ?? undefined}>
          {sessao.maquina}
          {sessao.projeto ? ' · ' + pastaCurta(sessao.projeto) : ''}
        </p>
      </div>
      <p className="col-span-2 min-w-0 truncate text-xs text-zinc-400 lg:col-span-1">
        {ROTULO_DO_MOTOR[sessao.motor]}
        {sessao.modelo ? ' · ' + sessao.modelo : ''}
        {sessao.esforco ? ' · ' + sessao.esforco : ''}
      </p>
      {formaDaConta(sessao.motor) === 'custo-real' ? (
        <p data-testid="sem-cota" className="col-span-2 min-w-0 truncate text-xs text-zinc-500 lg:col-span-1">
          {textoSemCota(sessao.modelo)}
        </p>
      ) : (
        <div className="col-span-2 flex min-w-0 items-center gap-2 lg:col-span-1">
          <BarraDeCota
            utilizacao={sessao.pctSemanal}
            rotulo={'Parte da semana de ' + sessao.titulo}
            className="w-24 lg:w-auto lg:flex-1"
          />
          <span
            data-testid="pct-da-sessao"
            className={'shrink-0 text-xs tabular-nums ' + (sessao.pctSemanal === null ? 'text-zinc-500' : 'text-zinc-200')}
          >
            {sessao.pctSemanal === null ? 'sem dado' : porcento(sessao.pctSemanal)}
          </span>
        </div>
      )}
      <Celula rotulo={rotuloDoCusto(sessao.motor, sessao.modelo)}>
        <span data-testid="custo-da-sessao">{custoEmTexto(sessao.custoEquivUsd) ?? '—'}</span>
        <SeloAssinatura assinatura={sessao.assinatura} className="ml-1" />
      </Celula>
      <Celula rotulo="contexto">{contextoEmTexto(sessao.contextoTokens, sessao.contextoLimite) ?? '—'}</Celula>
      <Celula rotulo="chamadas">{sessao.chamadas.toLocaleString('pt-BR')}</Celula>
      <Celula rotulo="última atividade">{tempoRelativo(sessao.ultimaAtividadeEm, 'sem atividade')}</Celula>
    </li>
  );
}

/** O "como é calculado", por extenso. A tela não esconde que o número é estimado. */
function ExplicacaoDoRateio({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }): React.JSX.Element | null {
  return (
    <Modal titulo="Como o % por sessão é estimado" aberto={aberto} aoFechar={aoFechar}>
      <div className="space-y-2.5">
        <p>
          A cota é da <strong>conta</strong>, não da sessão: o Claude e o Codex informam quanto da
          semana da conta foi usado, e nada por sessão.
        </p>
        <p>
          O painel divide esse % entre as sessões que gastaram NAQUELA conta, na proporção do peso
          de cada uma desde o começo da semana dela: <em>% da sessão = % da conta × peso da sessão ÷
          peso de todas as sessões da conta</em>. Cada evento guarda a conta em que a máquina estava
          logada quando ele aconteceu; a sessão que trocou de conta no meio da semana aparece em cada
          uma, com o que gastou nela.
        </p>
        <p>
          <strong>Peso no Claude:</strong> o custo em preço de API (entrada, saída e cache, por
          modelo). Modelo sem preço conhecido pesa zero. É só um peso — a assinatura não cobra por
          token.
        </p>
        <p>
          <strong>Peso no Codex:</strong> entrada nova + {String(PESO_CODEX.cache).replace('.', ',')}{' '}
          × entrada em cache + {PESO_CODEX.saida} × saída, em tokens. O Codex não informa dinheiro,
          e por isso a coluna de custo dele fica vazia.
        </p>
        <p>
          <strong>No OpenCode não há rateio.</strong> Com o NanoGPT, a assinatura é da conta e
          aparece no cartão dele, com o saldo e o gasto em crédito; a coluna de custo de cada sessão
          é o <em>equivalente</em> em preço de tabela — a chamada coberta pela assinatura não custa
          dinheiro —, com o selo “assinatura” quando o modelo é incluído nela. Com o OpenRouter, a
          coluna é o valor <em>cobrado</em>, relatado a cada turno. Nos dois casos a linha não mostra
          %.
        </p>
        <p className="text-zinc-400">
          Por isso é estimativa: a assinatura pode pesar os modelos de outro jeito, e o que se usa
          fora do painel (claude.ai, outro aparelho) entra no % da conta sem aparecer em sessão
          nenhuma. Sem cota conhecida da conta, a sessão mostra “sem dado”, nunca 0%.
        </p>
      </div>
    </Modal>
  );
}

/** Quem gastou a semana DESTA conta. */
function SessoesDaConta({ sessoes }: { sessoes: SessaoDoPainel[] }): React.JSX.Element {
  return (
    <div className="border-t border-zinc-800/70 pt-3">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Quem gastou a semana</h3>
      {sessoes.length === 0 ? (
        <p className="text-sm text-zinc-500">Nenhuma sessão gastou nesta conta nesta semana.</p>
      ) : (
        <>
          <div
            aria-hidden="true"
            className={'hidden px-2 pb-1 text-[11px] uppercase tracking-wide text-zinc-600 ' + GRADE}
          >
            <span>sessão</span>
            <span>motor · modelo</span>
            <span>% da semana (estimativa)</span>
            <span>custo</span>
            <span>contexto</span>
            <span>chamadas</span>
            <span>atividade</span>
          </div>
          <ul className="space-y-2 lg:space-y-0">
            {sessoes.map((sessao) => (
              <LinhaDaSessao key={sessao.sessaoId + '|' + sessao.contaId} sessao={sessao} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function CartaoDaConta({ conta, sessoes }: { conta: ContaDoPainel; sessoes: SessaoDoPainel[] }): React.JSX.Element {
  const custoReal = formaDaConta(conta.motor) === 'custo-real';
  return (
    <Cartao
      titulo={tituloDaConta(conta)}
      acao={
        custoReal ? (
          <SeloForaDeCasa provedor={conta.provedorOpencode ?? null} />
        ) : conta.plano ? (
          <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[11px] text-zinc-400">plano {conta.plano}</span>
        ) : undefined
      }
    >
      <div data-testid="conta" data-conta={conta.id} data-motor={conta.motor} className="space-y-3">
        {custoReal ? (
          <ContaDoOpencode conta={conta} />
        ) : (
          <>
            <MaquinasDaConta conta={conta} />
            <CotaDaConta conta={conta} />
          </>
        )}
        <SessoesDaConta sessoes={sessoes} />
      </div>
    </Cartao>
  );
}

/** O motor de cota que ainda não tem conta nenhuma na tela: o cartão diz, em vez de sumir. */
function MotorSemConta({ motor }: { motor: Motor }): React.JSX.Element {
  return (
    <Cartao titulo={ROTULO_DO_MOTOR[motor]}>
      <div data-testid="motor-sem-conta" data-motor={motor}>
        <MedidorDeCota
          rotulo="Semana"
          utilizacao={null}
          reiniciaEm={null}
          detalhe={`nenhuma máquina informou uma conta do ${ROTULO_DO_MOTOR[motor]} ainda`}
        />
      </div>
    </Cartao>
  );
}

function PorConta({ painel }: { painel: UsoPainel }): React.JSX.Element {
  const [motor, setMotor] = useState<FiltroDeMotor>('TODOS');
  const [explicando, setExplicando] = useState(false);
  const motores = MOTORES.filter((m) => motor === 'TODOS' || m === motor);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-zinc-500">
          O % de cada sessão é estimativa: divide o % semanal DA CONTA dela pelo peso dela na
          semana.{' '}
          <button
            type="button"
            onClick={() => setExplicando(true)}
            className="underline hover:text-zinc-300 pointer-coarse:min-h-11"
          >
            como é calculado
          </button>
        </p>
        <CampoDeEscolha
          rotulo="Motor"
          rotuloOculto
          compacto
          valor={motor}
          opcoes={OPCOES_DE_MOTOR}
          aoEscolher={setMotor}
        />
      </div>
      {motores.map((m) => {
        const contas = painel.contas.filter((c) => c.motor === m);
        if (contas.length === 0) return cartaoSempreVisivel(m) ? <MotorSemConta key={m} motor={m} /> : null;
        return contas.map((conta) => (
          <CartaoDaConta
            key={conta.id}
            conta={conta}
            sessoes={painel.sessoes.filter((s) => s.contaId === conta.id).sort(porFatia)}
          />
        ));
      })}
      <ExplicacaoDoRateio aberto={explicando} aoFechar={() => setExplicando(false)} />
    </>
  );
}

function Uso(): React.JSX.Element {
  const { data: painel, error, isLoading } = usarUsoDoPainel();

  return (
    <Tela>
      <h1 className="text-lg font-medium text-zinc-100">Uso</h1>

      {isLoading ? <p className="text-sm text-zinc-500">carregando…</p> : null}
      {error ? (
        <p role="alert" className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-200">
          {mensagemDoErro(error, 'Não foi possível carregar o uso agora.')}
        </p>
      ) : null}

      {painel ? (
        <>
          <section aria-label="Uso por conta" className="space-y-4">
            <PorConta painel={painel} />
          </section>
        </>
      ) : null}
    </Tela>
  );
}

export const rotaUso = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/uso',
  component: Uso,
});
